import { getAssetTypes, getOwners } from "api/app.api";
import { getWorkgroups } from "api/config/workgroup";
import { action, computed, makeObservable, observable } from "mobx";
import { RootStore } from "stores/RootStore";
import { LabelValuePair } from "utils/common";
import { AssetPermissions, AuthorizePermissions, DevicePermissions, PeoplePermissions, SecurityPermissions, SystemAdminPermission, WorkgroupPermissions } from "views/config/roles/RoleConfigDto";
import { WorkgroupDto } from "views/config/workgroups/WorkgroupDto";

export class UserRolePermissionStore {
  root: RootStore;

  loading = true;
  private asset_types: LabelValuePair[] = [];
  private asset_owners: LabelValuePair[] = [];
  workgroups: WorkgroupDto[] = [];

  permissions!: AuthorizePermissions;

  constructor(root: RootStore) {
    this.root = root;
    makeObservable(this, {
      permissions: observable,
      loading: observable,

      getAssetTypes: action.bound,
      getAssetOwners: action.bound,

      haveAccessToPortal: computed,
      canViewUsers: computed,
      canReportUsers: computed,

      AllAssetTypes: computed,
      AllAssetOwners: computed,

      canViewAssets: computed,
      canManageAssets: computed,
      canReportAssets: computed,
      canViewWorkgroupRoles: computed,
      canManageWorkgroupRoles: computed,
      getWorkgroupsWithViewRolePermission: computed,
      getWorkgroupsWithManageRolePermission: computed
    });

  }

  get AllAssetTypes() {
    return this.asset_types
  }

  get AllAssetOwners() {
    return this.asset_owners
  }

  async loadPermissions() {

    this.permissions = this.root.webConfig.permissions
    return Promise.all([
      this.canViewAssets ? getAssetTypes() : [],
      this.canViewAssets ? getOwners() : [],
      this.canViewConfig || this.canManageGroupsAndRoles || this.canViewWorkgroupRoles
        ? getWorkgroups() : Promise.resolve([])
    ])
      .then(res => {
        this.asset_types = res[0]
        this.asset_owners = res[1]
        this.workgroups = res[2]
        this.loading = false
      })
  }

  get canManageSystemAdministrators() {
    return this.permissions.system_admin_permissions.permissions.includes(SystemAdminPermission.ManageSystemAdmins);
  }

  get canManageUsers() {
    return this.permissions.system_admin_permissions.permissions.includes(SystemAdminPermission.ManageUsers);
  }

  get canManageGroupsAndRoles() {
    return this.permissions.system_admin_permissions.permissions.includes(SystemAdminPermission.ManageGroupsAndRoles)
  }

  get canViewConfig() {
    return this.permissions.system_admin_permissions.permissions.includes(SystemAdminPermission.ViewSystemConfiguration)
  }

  get haveAccessToPortal() {
    return this.permissions?.security_permissions.permissions.includes(SecurityPermissions.access_portal)
  }

  get canViewUsers() {
    return PeoplePermissions.view in this.permissions.localized_user_permissions.permission_to_boundary
  }

  get canReportUsers() {
    return PeoplePermissions.reports in this.permissions.localized_user_permissions.permission_to_boundary
  }

  get canViewAssets() {
    return AssetPermissions.view in this.permissions.asset_permissions.permission_to_boundary
  }

  get canViewDevices() {
    return this.root.hasDevicesFeature &&
      this.permissions.device_permissions.permissions.includes(DevicePermissions.view)
  }

  get canManageAssets() {
    return AssetPermissions.manage in this.permissions.asset_permissions.permission_to_boundary
  }

  get canReportAssets() {
    return AssetPermissions.reports in this.permissions.asset_permissions.permission_to_boundary
  }

  get canReportDevices() {
    return this.permissions.device_permissions.permissions.includes(DevicePermissions.reports)
  }

  get canViewWorkgroupRoles() {
    return WorkgroupPermissions.view_roles in this.permissions.workgroup_admin_permissions.permission_to_boundary
  }

  get canManageWorkgroupRoles() {
    return WorkgroupPermissions.create_and_manage_roles in this.permissions.workgroup_admin_permissions.permission_to_boundary
  }

  get getWorkgroupsWithViewRolePermission(): WorkgroupDto[] {
    const group_permissions = this.permissions.workgroup_admin_permissions.permission_to_boundary
    if (!(WorkgroupPermissions.view_roles in group_permissions)) {
      return []
    }
    const accessed = group_permissions[WorkgroupPermissions.view_roles].workgroup_ids
    if (accessed === null)
      return this.workgroups
    return this.workgroups.filter(r => accessed?.includes(r.id))
  }

  get getWorkgroupsWithManageRolePermission(): WorkgroupDto[] {
    const group_permissions = this.permissions.workgroup_admin_permissions.permission_to_boundary
    if (!(WorkgroupPermissions.create_and_manage_roles in group_permissions)) {
      return []
    }
    const accessed = group_permissions[WorkgroupPermissions.create_and_manage_roles].workgroup_ids
    if (accessed === null)
      return this.workgroups
    return this.workgroups.filter(r => accessed?.includes(r.id))
  }

  getAssetTypes(p: AssetPermissions) {
    const asset_permissions = this.permissions.asset_permissions.permission_to_boundary
    if (!(p in asset_permissions)) {
      return []
    }
    const accessed = asset_permissions[p].asset_type_ids
    if (accessed === null)
      return this.asset_types
    return this.asset_types.filter(r => accessed?.includes(r.value))
  }

  getAssetOwners(p: AssetPermissions) {
    const asset_permissions = this.permissions.asset_permissions.permission_to_boundary
    if (!(p in asset_permissions)) {
      return []
    }
    const accessed = asset_permissions[p].asset_owner_ids
    if (accessed === null)
      return this.asset_owners
    return this.asset_owners.filter(r => accessed?.includes(r.value))
  }

}
